// frontend/src/pages/EmailListPage.tsx - REDESIGNED WITH SPLIT-PANE PREVIEW
// Enhanced with right-side preview pane, HTML email rendering, and Move to Correspondence

import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn, formatDate, truncateText } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';
import { Mail } from 'lucide-react';
import apiClient from '@/api';

// Hooks
import {
  useEmails,
  useEmail,
  useConvertEmailToCorrespondence,
  useMarkEmailAsRead,
  usePreserveEmail,
  useDeleteEmail
} from '@/hooks/useEmails';

import { useProjects } from '@/hooks/useProjects';

// Types
import type {
  Email,
  EmailQueryParams,
  EmailFolder,
  EmailRetentionStatus,
  EmailProcessingStatus,
  Project
} from '@/api/types';

// ===== UTILITY COMPONENTS =====

// Simple email list item
const EmailListItem: React.FC<{
  email: Email;
  selected: boolean;
  onClick: (email: Email) => void;
}> = memo(({ email, selected, onClick }) => {
  const fromValue = email.from;
  const fromEmail = typeof fromValue === 'string' ? fromValue : (fromValue?.email || 'Unknown');
  const fromName = typeof fromValue === 'string' ? fromValue : (fromValue?.name || fromEmail);

  // Extract email account info - use provider field
  const providerType = email.provider;

  // Determine email type/folder
  const folderType = email.folder || 'inbox';

  // Check if email is important (using isImportant field)
  const isImportant = email.isImportant;

  return (
    <div
      onClick={() => onClick(email)}
      className={cn(
        'p-4 border-l-4 cursor-pointer transition-colors',
        selected
          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
          : 'bg-white dark:bg-gray-800 border-transparent hover:bg-gray-50 dark:hover:bg-gray-700',
        'border-b border-gray-200 dark:border-gray-700'
      )}
    >
      {/* Header with account and date */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <h3 className={cn(
            'text-sm font-medium truncate',
            isImportant ? 'font-bold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
          )}>
            {fromName}
          </h3>

          {/* Email type badge (Sent) */}
          {folderType === 'sent' && (
            <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Sent
            </span>
          )}
          {folderType === 'drafts' && (
            <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
              Draft
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 whitespace-nowrap">
          {formatDate(email.date)}
        </span>
      </div>

      {/* Subject */}
      <p className={cn(
        'text-sm mb-1 truncate',
        isImportant ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
      )}>
        {email.subject || '(No Subject)'}
      </p>

      {/* Body snippet */}
      <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
        {truncateText((email.snippet || (email.body || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()), 100)}
      </p>

      {/* Account and metadata badges */}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {/* Provider badge */}
        {providerType && (
          <span className={cn(
            "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
            providerType === 'gmail' ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300' :
            providerType === 'outlook' ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300' :
            'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
          )}>
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
            </svg>
            {providerType.charAt(0).toUpperCase() + providerType.slice(1)}
          </span>
        )}

        {email.hasAttachments && (
          <span className="text-xs text-gray-500 flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            {Array.isArray(email.attachments) ? email.attachments.length : 1}
          </span>
        )}

        {email.correspondences && email.correspondences.length > 0 && (
          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 rounded flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {email.correspondences.length}
          </span>
        )}

        {email.preservationStatus === 'preserved' && (
          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300 rounded">
            Preserved
          </span>
        )}

        {/* Response status badges */}
        {Array.isArray(email.correspondences) && email.correspondences.length > 0 && (() => {
          // Find correspondences with responses
          // Note: email.correspondences is array of EmailCorrespondenceLink {correspondenceId, projectId, ...}
          // where correspondenceId can be populated as a Correspondence object
          const correspondencesWithResponses = email.correspondences.filter((link: any) => {
            // Check if this is an EmailCorrespondenceLink with populated correspondenceId
            if (link.correspondenceId && typeof link.correspondenceId === 'object') {
              const corr = link.correspondenceId;
              return corr.responseId && typeof corr.responseId === 'object';
            }
            return false;
          });

          if (correspondencesWithResponses.length === 0) return null;

          // Get the first response (for display - could aggregate multiple later)
          const firstCorr = (correspondencesWithResponses[0] as any).correspondenceId;
          const firstResponse = firstCorr.responseId;

          // Response status badge
          const getStatusBadge = (status: string) => {
            switch (status) {
              case 'draft':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Draft
                </span>;
              case 'in_review':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  In Review
                </span>;
              case 'approved':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Approved
                </span>;
              case 'sent':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Sent
                </span>;
              case 'scheduled':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Scheduled
                </span>;
              case 'cancelled':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Cancelled
                </span>;
              default:
                return null;
            }
          };

          return (
            <>
              {getStatusBadge(firstResponse.status)}

              {/* Draft lock indicator */}
              {firstResponse.draftLock?.isLocked && firstResponse.draftLock.lockedBy && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300" title={`Being edited by ${firstResponse.draftLock.lockedBy.username || firstResponse.draftLock.lockedBy.email}`}>
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Editing
                </span>
              )}

              {/* Sent/Closed indicator */}
              {firstResponse.sentAt && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300" title={`Sent on ${new Date(firstResponse.sentAt).toLocaleString()}`}>
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Sent {formatDate(firstResponse.sentAt, false)}
                </span>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Re-render if email ID changes, selection state changes, or onClick changes
  return (
    (prevProps.email._id || prevProps.email.id) === (nextProps.email._id || nextProps.email.id) &&
    prevProps.selected === nextProps.selected
  );
});

EmailListItem.displayName = 'EmailListItem';

// Collapsible email list component
const CollapsibleEmailList: React.FC<{
  emails: string[];
  maxVisible?: number;
}> = ({ emails, maxVisible = 2 }) => {
  const [expanded, setExpanded] = useState(false);

  if (emails.length === 0) return null;

  const visibleEmails = expanded ? emails : emails.slice(0, maxVisible);
  const hiddenCount = emails.length - maxVisible;

  return (
    <span className="text-gray-900 dark:text-white">
      {visibleEmails.join(', ')}
      {hiddenCount > 0 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="ml-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
        >
          +{hiddenCount} more
        </button>
      )}
      {expanded && hiddenCount > 0 && (
        <button
          onClick={() => setExpanded(false)}
          className="ml-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
        >
          show less
        </button>
      )}
    </span>
  );
};

// Email preview pane
const EmailPreviewPane: React.FC<{
  email: Email;
  loading: boolean;
  projects: Project[];
  onClose: () => void;
  onMoveToCorrespondence: (emailId: string, projectId?: string, customFields?: Array<{ fieldId: string; name: string; type: string; value: any }>, sourceDocument?: { type: 'attachment'; attachmentId: string }, selectedAttachmentIds?: string[]) => void;
  onMarkAsRead: (emailId: string) => void;
  onPreserve: (emailId: string) => void;
  onDelete: (emailId: string) => void;
}> = ({ email, loading, projects, onClose, onMoveToCorrespondence, onMarkAsRead, onPreserve, onDelete }) => {
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [headerExpanded, setHeaderExpanded] = useState(false);
  const [expandedAttachments, setExpandedAttachments] = useState<Set<number>>(new Set());
  const [correspondencesExpanded, setCorrespondencesExpanded] = useState(false);
  const [orgCustomFields, setOrgCustomFields] = useState<any[]>([]);
  // Source document: 'email' or an attachment ID
  const [primarySource, setPrimarySource] = useState<string>('email');
  // Attachment IDs to include in the correspondence (all selected by default)
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<Set<string>>(new Set());
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const { addNotification } = useAppStore();

  // Fetch org custom field definitions
  useEffect(() => {
    apiClient.organizationCustomization.getFields()
      .then((res: any) => {
        const fieldsData = res?.data || res;
        const fields = fieldsData?.effectiveCorrespondenceFields || [];
        setOrgCustomFields(fields);
      })
      .catch(() => setOrgCustomFields([]));
  }, []);

  // Pre-populate custom field values and attachment selection when opening modal
  useEffect(() => {
    if (showMoveModal && email) {
      const metadata = (email as any).classificationState?.extractedMetadata;
      const prefilled: Record<string, any> = {};
      if (metadata) {
        if (metadata.primaryDocumentNumber) prefilled['document_reference_number'] = metadata.primaryDocumentNumber;
        if (metadata.primaryDocumentDate) prefilled['document_date'] = metadata.primaryDocumentDate;
        if (metadata.senderOrganization) prefilled['received_from'] = metadata.senderOrganization;
      }
      setCustomFieldValues(prefilled);
      setPrimarySource('email');
      // Select all attachments by default
      if (email.hasAttachments && Array.isArray(email.attachments) && email.attachments.length > 0) {
        const allIds = new Set(email.attachments.map((a: any) => a._id || a.id || a.toString()));
        setSelectedAttachmentIds(allIds);
      } else {
        setSelectedAttachmentIds(new Set());
      }
    }
  }, [showMoveModal, email]);

  // Handle attachment download with authentication
  const handleDownloadAttachment = async (attachment: any) => {
    try {
      const attachmentId = attachment._id || attachment.id;
      const attachmentFilename = attachment.filename || attachment.originalName || 'attachment';


      // Check if attachment has s3Key - might need different download approach
      if (!attachment.s3Url && !attachment.s3Key) {
        addNotification({ type: 'warning', title: 'Cannot Download', message: 'This attachment has no storage location.' });
        return;
      }

      // Download using apiClient which includes auth headers
      const blob = await apiClient.attachments.download(attachmentId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachmentFilename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error: any) {
      console.error('Failed to download attachment:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });

      let errorMessage = 'Failed to download attachment';
      if (error.response?.status === 404) {
        // Check if this email has been converted to correspondence(s)
        const hasCorrespondences = email.correspondences && email.correspondences.length > 0;

        errorMessage = `Attachment is no longer available from this email.\n\n` +
          `Email attachments are stored temporarily and:\n` +
          `• Auto-deleted after 90 days (retention policy)\n` +
          `• Moved to permanent storage when converted to correspondence\n\n`;

        if (hasCorrespondences) {
          errorMessage += `✓ This email was converted to ${email.correspondences.length} correspondence(s).\n` +
            `The attachment is permanently stored with the correspondence.\n\n` +
            `📎 Download from the "Converted to Correspondences" section below.`;
        } else {
          errorMessage += `If this email was converted to a correspondence, the attachment\n` +
            `will be available on the Correspondences page.`;
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      addNotification({ type: 'error', title: 'Download Failed', message: errorMessage });
    }
  };

  const fromValue = email.from;
  const fromEmail = typeof fromValue === 'string' ? fromValue : (fromValue?.email || 'Unknown');
  const fromName = typeof fromValue === 'string' ? fromValue : (fromValue?.name || fromEmail);

  const toEmails = Array.isArray(email.to)
    ? email.to.map(t => typeof t === 'string' ? t : (t?.email || '')).filter(Boolean)
    : [];

  const ccEmails = email.cc && Array.isArray(email.cc)
    ? email.cc.map(c => typeof c === 'string' ? c : (c?.email || '')).filter(Boolean)
    : [];

  // Clear expanded attachments when email changes
  useEffect(() => {
    setExpandedAttachments(new Set());
  }, [email._id || email.id]);

  useEffect(() => {
    // Check if email has HTML body - check multiple possible field names
    const possibleHtmlFields = [
      (email as any).htmlBody,
      (email as any).html,
      (email as any).bodyHtml,
      (email as any).htmlContent
    ];

    const htmlBody = possibleHtmlFields.find(field => field && field.trim());

    if (htmlBody) {
      setHtmlContent(htmlBody);
    } else if (email.body) {
      // Check if body is already HTML (contains HTML tags)
      const isHtml = /<\s*(html|head|body|div|p|br|table|span)\b/i.test(email.body);
      if (isHtml) {
        setHtmlContent(email.body);
        return;
      }
      // Convert plain text to HTML with clickable links
      const textWithLinks = email.body
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
        // Convert URLs to clickable links
        .replace(
          /(https?:\/\/[^\s<]+[^<.,;:!?\s])/gi,
          '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline dark:text-blue-400">$1</a>'
        )
        // Convert email addresses to mailto links
        .replace(
          /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi,
          '<a href="mailto:$1" class="text-blue-600 hover:underline dark:text-blue-400">$1</a>'
        );
      setHtmlContent(textWithLinks);
    } else {
      setHtmlContent('');
    }
  }, [email]);

  const handleMove = () => {
    // Build custom fields array from form values
    const customFields = orgCustomFields
      .filter((f: any) => customFieldValues[f.fieldId] !== undefined && customFieldValues[f.fieldId] !== '' && customFieldValues[f.fieldId] !== null)
      .map((f: any) => ({
        fieldId: f.fieldId,
        name: f.name,
        type: f.type,
        value: customFieldValues[f.fieldId]
      }));

    // Build source document info
    const sourceDocument = primarySource === 'email'
      ? undefined
      : { type: 'attachment' as const, attachmentId: primarySource };

    // Build selected attachment IDs array
    const attachmentIds = selectedAttachmentIds.size > 0 ? Array.from(selectedAttachmentIds) : undefined;

    onMoveToCorrespondence(
      email._id || email.id!,
      selectedProjectId || undefined,
      customFields.length > 0 ? customFields : undefined,
      sourceDocument,
      attachmentIds
    );
    setShowMoveModal(false);
    setSelectedProjectId('');
    setCustomFieldValues({});
    setPrimarySource('email');
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
              {email.subject || '(No Subject)'}
            </h2>

            {/* Compact view - shows from and date only */}
            {!headerExpanded && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">{fromName}</span> • {formatDate(email.date)}
                {email.hasAttachments && (
                  <span className="ml-2">
                    📎 {Array.isArray(email.attachments) ? email.attachments.length : 0}
                  </span>
                )}
              </div>
            )}

            {/* Expanded view - shows all details */}
            {headerExpanded && (
              <div className="space-y-1 text-sm mt-2">
                <div className="flex items-start">
                  <span className="font-medium text-gray-500 dark:text-gray-400 w-16">From:</span>
                  <span className="text-gray-900 dark:text-white">{fromName} &lt;{fromEmail}&gt;</span>
                </div>
                <div className="flex items-start">
                  <span className="font-medium text-gray-500 dark:text-gray-400 w-16">To:</span>
                  <CollapsibleEmailList emails={toEmails} maxVisible={2} />
                </div>
                {ccEmails.length > 0 && (
                  <div className="flex items-start">
                    <span className="font-medium text-gray-500 dark:text-gray-400 w-16">Cc:</span>
                    <CollapsibleEmailList emails={ccEmails} maxVisible={2} />
                  </div>
                )}
                <div className="flex items-start">
                  <span className="font-medium text-gray-500 dark:text-gray-400 w-16">Date:</span>
                  <span className="text-gray-900 dark:text-white">{formatDate(email.date)}</span>
                </div>
              </div>
            )}

            {/* Toggle button */}
            <button
              onClick={() => setHeaderExpanded(!headerExpanded)}
              className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1"
            >
              {headerExpanded ? (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                  Show less
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Show details
                </>
              )}
            </button>
          </div>

          <Button variant="ghost" size="sm" onClick={onClose} className="flex-shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() => setShowMoveModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Move to Correspondence
          </Button>
          {email.preservationStatus !== 'preserved' && (
            <Button size="sm" variant="outline" onClick={() => onPreserve(email._id || email.id!)}>
              Preserve
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onDelete(email._id || email.id!)}>
            Delete
          </Button>
        </div>

        {/* Correspondences Section - Compact with expand */}
        {email.correspondences && email.correspondences.length > 0 && (() => {
          const MAX_VISIBLE = 2;
          const allCorrespondences = email.correspondences;
          const visibleCorrespondences = correspondencesExpanded ? allCorrespondences : allCorrespondences.slice(0, MAX_VISIBLE);
          const hiddenCount = allCorrespondences.length - MAX_VISIBLE;
          return (
          <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Correspondences ({allCorrespondences.length})
              </h3>
              {hiddenCount > 0 && (
                <button
                  onClick={() => setCorrespondencesExpanded(!correspondencesExpanded)}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
                >
                  {correspondencesExpanded ? 'Show less' : `+${hiddenCount} more`}
                </button>
              )}
            </div>
            <div className="space-y-1">
              {visibleCorrespondences.map((correspondence: any, index: number) => {
                const corrId = typeof correspondence.correspondenceId === 'object'
                  ? correspondence.correspondenceId?._id
                  : correspondence.correspondenceId;
                return (
                <a
                  key={corrId || index}
                  href={`/correspondences/${corrId}`}
                  className="flex items-center gap-2 px-2.5 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm"
                >
                  <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-blue-800 dark:text-blue-200 truncate flex-1">
                    {correspondence.isPersonal ? 'Personal' : correspondence.projectName || 'Project'}
                  </span>
                  <svg className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
                );
              })}
            </div>
          </div>
          );
        })()}
      </div>

      {/* Email Body */}
      <div className="flex-1 overflow-auto p-6">
        {htmlContent ? (
          <div
            className="prose prose-sm max-w-none dark:prose-invert prose-a:text-blue-600 hover:prose-a:text-blue-700 dark:prose-a:text-blue-400"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        ) : (
          <div className="text-gray-700 dark:text-gray-300 text-sm">
            No content available
          </div>
        )}

        {/* Attachments */}
        {email.hasAttachments && Array.isArray(email.attachments) && email.attachments.length > 0 && (
          <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Attachments ({email.attachments.length})
            </h3>
            <div className="space-y-2">
              {email.attachments.map((attachment: any, index: number) => {
                const isExpanded = expandedAttachments.has(index);
                const hasExtractedText = attachment.extractedContent && attachment.extractedContent.trim();
                const hasError = attachment.textExtractionStatus === 'skipped' || attachment.textExtractionStatus === 'failed';
                const canExpand = hasExtractedText || hasError;

                const toggleExpanded = () => {
                  setExpandedAttachments(prev => {
                    const next = new Set(prev);
                    if (next.has(index)) {
                      next.delete(index);
                    } else {
                      next.add(index);
                    }
                    return next;
                  });
                };

                return (
                  <div
                    key={index}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                  >
                    <div className="flex items-center justify-between p-3 gap-2">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {attachment.filename || `Attachment ${index + 1}`}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {attachment.contentType} • {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                            </p>
                            {attachment.textExtractionStatus === 'completed' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                Text Extracted
                              </span>
                            )}
                            {attachment.textExtractionStatus === 'skipped' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                Skipped
                              </span>
                            )}
                            {attachment.textExtractionStatus === 'failed' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                Failed
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {canExpand && (
                          <button
                            onClick={toggleExpanded}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors flex items-center gap-1"
                            title={isExpanded ? "Hide preview" : "Show preview"}
                          >
                            {isExpanded ? (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                                Hide
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                                Preview
                              </>
                            )}
                          </button>
                        )}
                        {attachment.s3Url && (
                          <button
                            onClick={() => handleDownloadAttachment(attachment)}
                            className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                          >
                            Download
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Collapsible Extracted Text Display */}
                    {isExpanded && hasExtractedText && (
                      <div className="border-t border-gray-200 dark:border-gray-600 p-3 bg-white dark:bg-gray-800">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                            Extracted Text Preview
                          </h4>
                          <div className="flex items-center gap-2">
                            {attachment.metadata?.contentTruncated && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Showing {attachment.extractedContent.length.toLocaleString()} of {attachment.metadata.originalLength.toLocaleString()} characters
                              </span>
                            )}
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(attachment.extractedContent);
                                const btn = document.getElementById(`copy-btn-${index}`);
                                if (btn) { btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy'; }, 2000); }
                              }}
                              id={`copy-btn-${index}`}
                              className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded border border-gray-300 dark:border-gray-500 transition-colors"
                            >
                              Copy
                            </button>
                          </div>
                        </div>

                        {/* Partial extraction warning */}
                        {attachment.metadata?.partialExtraction && (
                          <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                            <p className="text-xs text-blue-800 dark:text-blue-200">
                              <span className="font-semibold">Partial Preview:</span>
                              {attachment.metadata.extractedPages && attachment.metadata.totalPages ? (
                                <> Showing first {attachment.metadata.extractedPages} of {attachment.metadata.totalPages} pages. Download file for complete content.</>
                              ) : attachment.metadata.extractedLength && attachment.metadata.originalLength ? (
                                <> Showing first {attachment.metadata.extractedLength.toLocaleString()} of {attachment.metadata.originalLength.toLocaleString()} characters. Download file for complete content.</>
                              ) : attachment.metadata.sheetsSkipped ? (
                                <> Showing first sheet only ({attachment.metadata.sheetsSkipped} sheets skipped). Download file for complete content.</>
                              ) : (
                                <> Large file - showing preview only. Download file for complete content.</>
                              )}
                            </p>
                          </div>
                        )}

                        <div className="bg-gray-50 dark:bg-gray-900 rounded max-h-96 overflow-y-auto">
                          {attachment.extractedContent.split(/(?=---\s*Page\s+\d+\s*---)/).map((section: string, sIdx: number) => {
                            const pageMatch = section.match(/^---\s*Page\s+(\d+)\s*---\n?/);
                            const content = pageMatch ? section.replace(/^---\s*Page\s+\d+\s*---\n?/, '') : section;
                            const trimmed = content.replace(/\n{3,}/g, '\n\n').trim();
                            if (!trimmed) return null;
                            return (
                              <div key={sIdx}>
                                {pageMatch && (
                                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0">
                                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                                      Page {pageMatch[1]}
                                    </span>
                                  </div>
                                )}
                                <div className="text-xs text-gray-700 dark:text-gray-300 p-3 whitespace-pre-wrap leading-relaxed">
                                  {trimmed}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {attachment.metadata?.contentTruncated && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">
                            Content has been truncated. Download the file to view the complete text.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Collapsible Extraction Skipped/Failed Message */}
                    {isExpanded && attachment.textExtractionStatus === 'skipped' && attachment.textExtractionError && (
                      <div className="border-t border-gray-200 dark:border-gray-600 p-3 bg-yellow-50 dark:bg-yellow-900/20">
                        <p className="text-xs text-yellow-800 dark:text-yellow-200">
                          <span className="font-semibold">Text extraction skipped:</span> {attachment.textExtractionError}
                        </p>
                      </div>
                    )}

                    {isExpanded && attachment.textExtractionStatus === 'failed' && attachment.textExtractionError && (
                      <div className="border-t border-gray-200 dark:border-gray-600 p-3 bg-red-50 dark:bg-red-900/20">
                        <p className="text-xs text-red-800 dark:text-red-200">
                          <span className="font-semibold">Text extraction failed:</span> {attachment.textExtractionError}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Move to Correspondence Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[85vh] flex flex-col">
            <div className="p-6 overflow-y-auto flex-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Move to Correspondence
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select a project and fill in correspondence details.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project (Optional)
                </label>
                <select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No Project (Personal)</option>
                  {projects.map(project => (
                    <option key={project._id || project.id} value={project._id || project.id}>
                      {(project as any).projectName || project.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Primary Source - choose what drives the correspondence */}
              {email.hasAttachments && Array.isArray(email.attachments) && email.attachments.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    Primary Source
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Choose whether the correspondence is based on the email or one of its attachments.
                  </p>
                  <div className="space-y-2">
                    <label className={cn(
                      'flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors',
                      primarySource === 'email'
                        ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    )}>
                      <input
                        type="radio"
                        name="primarySource"
                        value="email"
                        checked={primarySource === 'email'}
                        onChange={() => setPrimarySource('email')}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Email</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{email.subject || 'No subject'}</p>
                      </div>
                    </label>
                    {email.attachments.map((att: any) => {
                      const attId = att._id || att.id || att.toString();
                      const hasOCR = !!(att.extractedContent && att.extractedContent.trim());
                      return (
                        <label key={attId} className={cn(
                          'flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors',
                          primarySource === attId
                            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        )}>
                          <input
                            type="radio"
                            name="primarySource"
                            value={attId}
                            checked={primarySource === attId}
                            onChange={() => setPrimarySource(attId)}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate block">{att.filename || 'Attachment'}</span>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {att.contentType} • {((att.size || 0) / 1024).toFixed(1)} KB
                              {hasOCR ? (
                                <span className="ml-1.5 text-green-600 dark:text-green-400">• Text extracted</span>
                              ) : (
                                <span className="ml-1.5 text-amber-500 dark:text-amber-400">• Email body will be used</span>
                              )}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Attachments to Include */}
              {email.hasAttachments && Array.isArray(email.attachments) && email.attachments.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Attachments to Include
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedAttachmentIds.size === email.attachments.length) {
                          setSelectedAttachmentIds(new Set());
                        } else {
                          setSelectedAttachmentIds(new Set(email.attachments.map((a: any) => a._id || a.id || a.toString())));
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
                    >
                      {selectedAttachmentIds.size === email.attachments.length ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Choose which attachments to include in the correspondence.
                  </p>
                  <div className="space-y-1.5">
                    {email.attachments.map((att: any) => {
                      const attId = att._id || att.id || att.toString();
                      const isSelected = selectedAttachmentIds.has(attId);
                      const isPrimarySource = primarySource === attId;
                      return (
                        <label key={attId} className={cn(
                          'flex items-center gap-2.5 px-2.5 py-2 rounded-lg border cursor-pointer transition-colors',
                          isSelected
                            ? 'border-blue-300 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-700'
                            : 'border-gray-200 dark:border-gray-600',
                          isPrimarySource && 'border-blue-300 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-700 cursor-not-allowed'
                        )}>
                          <input
                            type="checkbox"
                            checked={isPrimarySource || isSelected}
                            disabled={isPrimarySource}
                            onChange={() => {
                              if (isPrimarySource) return;
                              setSelectedAttachmentIds(prev => {
                                const next = new Set(prev);
                                if (next.has(attId)) {
                                  next.delete(attId);
                                } else {
                                  next.add(attId);
                                }
                                return next;
                              });
                            }}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-900 dark:text-white truncate block">{att.filename || 'Attachment'}</span>
                            {isPrimarySource && (
                              <span className="text-xs text-blue-600 dark:text-blue-400">Primary source — always included</span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Custom Fields */}
              {orgCustomFields.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                    Correspondence Fields
                  </h4>
                  <div className="space-y-3">
                    {orgCustomFields.filter((f: any) => f.fieldId !== 'document_age_days').map((field: any) => {
                      const fieldId = field.fieldId;
                      const value = customFieldValues[fieldId] ?? '';

                      if (field.type === 'select') {
                        return (
                          <div key={fieldId}>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              {field.name}
                            </label>
                            <select
                              value={value}
                              onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="">--</option>
                              {(field.options || []).map((opt: any) => (
                                <option key={opt.value || opt} value={opt.value || opt}>
                                  {opt.label || opt.value || opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      }

                      if (field.type === 'multi-select') {
                        const selectedValues: string[] = Array.isArray(value) ? value : [];
                        return (
                          <div key={fieldId}>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              {field.name}
                            </label>
                            <div className="flex flex-wrap gap-1">
                              {(field.options || []).map((opt: any) => {
                                const optVal = opt.value || opt;
                                const isSelected = selectedValues.includes(optVal);
                                return (
                                  <button
                                    key={optVal}
                                    type="button"
                                    onClick={() => {
                                      setCustomFieldValues(prev => ({
                                        ...prev,
                                        [fieldId]: isSelected
                                          ? selectedValues.filter(v => v !== optVal)
                                          : [...selectedValues, optVal]
                                      }));
                                    }}
                                    className={cn(
                                      'px-2 py-0.5 text-xs rounded-full border transition-colors',
                                      isSelected
                                        ? 'bg-blue-100 dark:bg-blue-900 border-blue-400 text-blue-700 dark:text-blue-300'
                                        : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-blue-300'
                                    )}
                                  >
                                    {opt.label || optVal}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }

                      if (field.type === 'date') {
                        return (
                          <div key={fieldId}>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              {field.name}
                            </label>
                            <input
                              type="date"
                              value={value}
                              onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        );
                      }

                      if (field.type === 'boolean') {
                        return (
                          <div key={fieldId} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={!!value}
                              onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.checked }))}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              {field.name}
                            </label>
                          </div>
                        );
                      }

                      // Default: text input (covers text, number, etc.)
                      return (
                        <div key={fieldId}>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {field.name}
                          </label>
                          <input
                            type={field.type === 'number' ? 'number' : 'text'}
                            value={value}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder={field.placeholder || ''}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowMoveModal(false);
                    setSelectedProjectId('');
                    setCustomFieldValues({});
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleMove}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Move to Correspondence
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Debounced value hook
function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ===== MAIN COMPONENT =====

export const EmailListPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();
  const { addNotification } = useAppStore();
  const { confirm } = useConfirmation();

  // Local state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [folderFilter, setFolderFilter] = useState<'all' | EmailFolder>('all');
  const [retentionFilter, setRetentionFilter] = useState<'all' | EmailRetentionStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEmailId, setSelectedEmailId] = useState<string | undefined>(id);

  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  // Build query params
  const queryParams = useMemo((): EmailQueryParams => {
    const params: EmailQueryParams = {
      page: currentPage,
      limit: 20,
      sortBy: 'date',
      sortOrder: 'desc'
    };

    if (debouncedSearchTerm.trim()) {
      params.search = debouncedSearchTerm.trim();
    }
    // Send 'all' explicitly: omitting it made the API default to the inbox,
    // which hid every sent email from the default view.
    params.section = folderFilter;
    if (retentionFilter !== 'all') {
      params.preservationStatus = retentionFilter;
    }

    return params;
  }, [debouncedSearchTerm, folderFilter, retentionFilter, currentPage]);

  // API hooks
  const {
    data: emailsData,
    loading: emailsLoading,
    refetch: refetchEmails
  } = useEmails(queryParams, { immediate: isAuthenticated });

  const {
    data: selectedEmail,
    loading: selectedEmailLoading
  } = useEmail(selectedEmailId, { immediate: !!selectedEmailId });

  const {
    data: projectsData
  } = useProjects({ limit: 100, status: 'active' }, { immediate: isAuthenticated });

  const { mutate: convertToCorrespondence } = useConvertEmailToCorrespondence();
  const { mutate: preserve } = usePreserveEmail();
  const { mutate: deleteEmail } = useDeleteEmail();

  const emails = emailsData?.data || [];
  const pagination = emailsData?.pagination;
  const projects = Array.isArray(projectsData) ? projectsData : projectsData?.data || [];
  const isLoading = emailsLoading;

  // Handlers
  const handleEmailSelect = useCallback((email: Email) => {
    const emailId = email._id || email.id;
    setSelectedEmailId(prevId => prevId === emailId ? undefined : emailId);
  }, []);

  const handleMoveToCorrespondence = useCallback(async (emailId: string, projectId?: string, customFields?: Array<{ fieldId: string; name: string; type: string; value: any }>, sourceDocument?: { type: 'attachment'; attachmentId: string }, selectedAttachmentIds?: string[]) => {
    try {
      await convertToCorrespondence({ emailId, projectId, customFields, sourceDocument, selectedAttachmentIds });
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Email moved to correspondence successfully'
      });
      refetchEmails();
      setSelectedEmailId(undefined);
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to move email to correspondence'
      });
    }
  }, [convertToCorrespondence, addNotification, refetchEmails]);

  const handleMarkAsRead = useCallback(async (emailId: string) => {
    // This function is for future use
    refetchEmails();
  }, [refetchEmails]);

  const handlePreserve = useCallback(async (emailId: string) => {
    try {
      await preserve({ emailIds: [emailId], preservationType: 'standard', reason: 'User preserved' });
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Email preserved successfully'
      });
      refetchEmails();
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to preserve email'
      });
    }
  }, [preserve, addNotification, refetchEmails]);

  const handleDelete = useCallback(async (emailId: string) => {
    const confirmed = await confirm({
      title: 'Delete Email',
      message: 'Are you sure you want to delete this email?',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteEmail(emailId);
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Email deleted successfully'
      });
      refetchEmails();
      setSelectedEmailId(undefined);
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to delete email'
      });
    }
  }, [deleteEmail, addNotification, refetchEmails]);

  const handleRefresh = useCallback(() => {
    refetchEmails();
  }, [refetchEmails]);

  // Render
  const pageActions = (
    <div className="flex items-center gap-1.5 sm:gap-2 flex-1 sm:flex-none justify-end">
      <div className="w-32 sm:w-40 md:w-48">
        <Input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full text-xs sm:text-sm py-1 sm:py-1.5 h-8"
        />
      </div>

      <select
        value={folderFilter}
        onChange={(e) => setFolderFilter(e.target.value as typeof folderFilter)}
        className="px-1.5 sm:px-2 py-1 sm:py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-8"
      >
        <option value="all">All</option>
        <option value="inbox">Inbox</option>
        <option value="sent">Sent</option>
        <option value="drafts">Drafts</option>
        <option value="trash">Trash</option>
      </select>

      <select
        value={retentionFilter}
        onChange={(e) => setRetentionFilter(e.target.value as typeof retentionFilter)}
        className="hidden sm:block px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-8"
      >
        <option value="all">All</option>
        <option value="temporary">Temp</option>
        <option value="preserved">Preserved</option>
        <option value="pending_deletion">Pending</option>
      </select>

      <Button
        variant="outline"
        onClick={handleRefresh}
        disabled={isLoading}
        size="sm"
        className="px-2 h-8 min-w-8"
        title="Refresh"
      >
        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </Button>
    </div>
  );

  return (
    <PageLayout
      title="Emails"
      actions={pageActions}
      compact
    >
      <div className="flex h-[calc(100vh-100px)]">
        {/* Email List - Left Side */}
        <div className={cn(
          'flex flex-col border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
          selectedEmailId ? 'hidden md:flex md:w-1/3' : 'w-full'
        )}>
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <LoadingSpinner size="lg" />
            </div>
          ) : emails.length > 0 ? (
            <div className="flex-1 overflow-y-auto">
              {emails.map((email) => (
                <EmailListItem
                  key={email._id || email.id}
                  email={email}
                  selected={selectedEmailId === (email._id || email.id)}
                  onClick={handleEmailSelect}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Mail size={24} />}
              title="No emails found"
              description="No emails match your current filters. Try adjusting your search or folder selection."
            />
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-4">
              <div className="flex justify-between items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={currentPage === pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Email Preview - Right Side (Full screen on mobile) */}
        {selectedEmailId && selectedEmail && (
          <div className={cn(
            'bg-white dark:bg-gray-800 overflow-hidden',
            'w-full md:flex-1'
          )}>
            <EmailPreviewPane
              email={selectedEmail}
              loading={selectedEmailLoading}
              projects={projects}
              onClose={() => setSelectedEmailId(undefined)}
              onMoveToCorrespondence={handleMoveToCorrespondence}
              onMarkAsRead={handleMarkAsRead}
              onPreserve={handlePreserve}
              onDelete={handleDelete}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
};
